import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import {CLStore} from 'common/src/redux/store';
import {z} from 'zod';
import updateAiContentProjectBrief from 'common/src/modules/aiContent/store/aiContentProject/thunks/updateAiContentProjectBrief';
import {AiContentProjectAgentToolName} from '../aiContentProjectAgent.enums';

const inputSchema = z.object({
  objective: z.string().optional().nullable().describe('The campaign objective or goal'),
  description: z
    .string()
    .optional()
    .nullable()
    .describe(
      'Detailed description of the campaign creative requirements, focusing on elements needed to be generated by AI into content feed',
    ),
  applyBrandGuidelines: z
    .boolean()
    .optional()
    .nullable()
    .describe('Whether to apply brand guidelines during content generation'),
});

export default function updateBriefTool(store: CLStore) {
  async function execute({
    input: {objective, description, applyBrandGuidelines},
  }: ToolExecuteParams<typeof inputSchema>): Promise<ToolResult> {
    const state = store.getState();
    const project = state.aiContentProjectPage?.project;

    if (!project?.id) {
      return toolError('No project is currently loaded. Cannot update brief.');
    }

    const currentBrief = project.brief;

    // Merge provided fields with existing brief
    const updatedBrief = {
      objective: currentBrief?.objective ?? null,
      description: currentBrief?.description ?? null,
      targetPlatforms: currentBrief?.targetPlatforms ?? null,
      personaIds: currentBrief?.personaIds ?? null,
      applyBrandGuidelines: currentBrief?.applyBrandGuidelines ?? false,
      ...(objective !== undefined && objective !== null && {objective}),
      ...(description !== undefined && description !== null && {description}),
      ...(applyBrandGuidelines !== undefined &&
        applyBrandGuidelines !== null && {applyBrandGuidelines}),
    };

    try {
      await store
        .dispatch(updateAiContentProjectBrief({projectId: project.id, brief: updatedBrief}))
        .unwrap();

      const updatedFields: string[] = [];

      if (objective !== undefined && objective !== null) {
        updatedFields.push('objective');
      }

      if (description !== undefined && description !== null) {
        updatedFields.push('description');
      }

      if (applyBrandGuidelines !== undefined && applyBrandGuidelines !== null) {
        updatedFields.push('applyBrandGuidelines');
      }

      return toolResult(
        `Successfully updated project brief. Updated fields: ${updatedFields.join(', ')}.`,
      );
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);

      return toolError(`Error updating brief: ${errorMessage}`);
    }
  }

  return tool({
    displayName: 'Updating brief',
    name: AiContentProjectAgentToolName.UpdateBrief,
    description:
      'Updates the AI Content Project brief with the specified values. The brief contains the objective, description, and brand guidelines settings.',
    execute,
    inputSchema,
  });
}
